import { isString } from 'lodash'
import {
  Button,
  Checkbox,
  Divider,
  Grid as MuiGrid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Menu,
  MenuItem,
  Paper as MuiPaper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@material-ui/core'
import {
  MoreVert as MoreVertIcon,
  GetApp as GetAppIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@material-ui/icons'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import FilterListIcon from '@material-ui/icons/FilterList'
import { Pagination } from '@material-ui/lab'
import { spacing } from '@material-ui/system'
import React, { useEffect, useState } from 'react'
import { NavLink as RouterNavLink, NavLinkProps } from 'react-router-dom'
import styled from 'styled-components'
import { useDebouncedCallback } from 'use-debounce'
import { ExportDialog } from 'src/components/Dialog'
import { ExportCall, ExportFilters } from 'src/services/skuit/types'
import { Filter, FilterDialog } from './FilterDialog'
import { Paginator } from './utils'

const NavLink = React.forwardRef((props: NavLinkProps, ref: React.Ref<HTMLAnchorElement>) => (
  <RouterNavLink innerRef={ref} {...props} />
))
NavLink.displayName = 'NavLink'

const Grid = styled(MuiGrid)(spacing)
const Paper = styled(MuiPaper)(spacing)

const Spacer = styled.div`
  flex: 1 1 100%;
`

const ToolbarTitle = styled.div`
  min-width: 150px;
`

type Identifier = string | number

type Ordering = 'none' | 'asc' | 'desc'

const nextOrder: { [key: string]: Ordering } = {
  none: 'asc',
  asc: 'desc',
  desc: 'none',
}

const directionMap: { [key: string]: false | 'asc' | 'desc' } = {
  none: false,
  asc: 'asc',
  desc: 'desc',
}

type RowTitleFunction = (row: any) => string
type RowActionFunction = (row: any) => void

export interface RowAction {
  title: string | RowTitleFunction
  onClick: RowActionFunction
}

interface GenericTabbleHeadProps {
  fields: FieldSpec[]
  bulkActions?: Action[]
  order: Ordering
  orderBy: Identifier
  numSelected: number
  rowCount: number
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void
  onRequestSort: (event: React.SyntheticEvent, property: Identifier) => void
  rowActions?: RowAction[]
  onSelectedItem?: (selected: any[]) => void
}

const GenericTableHead = (props: GenericTabbleHeadProps) => {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
    fields,
    bulkActions,
    rowActions,
    onSelectedItem,
  } = props
  const createSortHandler = (property: Identifier) => (event: React.SyntheticEvent) => {
    onRequestSort(event, property)
  }

  const direction = directionMap[order]

  return (
    <TableHead>
      <TableRow>
        {(onSelectedItem || bulkActions) && (
          <TableCell padding="checkbox">
            <Checkbox
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={rowCount > 0 && numSelected === rowCount}
              onChange={onSelectAllClick}
              inputProps={{ 'aria-label': 'select all' }}
            />
          </TableCell>
        )}
        {fields.map((field, index) => {
          if (!field) {
            return <TableCell key={`cell.misconfigured.${index}`}>Misconfigured Field</TableCell>
          }
          return (
            <TableCell
              key={`field.${field.id}`}
              align={field.alignment}
              sortDirection={orderBy === (field.ordering || field.id) ? direction : false}
            >
              <TableSortLabel
                active={direction && orderBy === (field.ordering || field.id)}
                direction={
                  direction && orderBy === (field.ordering || field.id) ? direction : 'asc'
                }
                onClick={createSortHandler(field.ordering || field.id)}
              >
                {field.label}
              </TableSortLabel>
            </TableCell>
          )
        })}
        {!!rowActions && <TableCell>Actions</TableCell>}
      </TableRow>
    </TableHead>
  )
}

interface ExportConfig {
  exportFields: string[]
  exportAPI: ExportCall
  exportFilters?: ExportFilters
}

interface TableToolbarProps {
  numSelected: number
  title: string
  paginator: Paginator
  actions?: { title: string; icon?: JSX.Element; onClick: () => void }[]
  filters?: Filter[]
  exportConfig?: ExportConfig
  onFilterApplied?: () => void
  bulkActions?: Action[]
  onSelectedBulkAction?: (action: string) => void
}

const TableToolbar = (props: TableToolbarProps) => {
  const {
    paginator,
    numSelected,
    title,
    actions,
    filters,
    exportConfig,
    onFilterApplied,
    bulkActions,
    onSelectedBulkAction,
  } = props
  const [filterDialogOpen, setFilterDialogOpen] = useState<boolean>(false)
  const [exportDialogOpen, setExportDialogOpen] = useState<boolean>(false)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const onSearchChange = useDebouncedCallback((term: string) => {
    paginator.patchFilters({ search: term })
    onFilterApplied?.()
  }, 200)

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  return (
    <Toolbar>
      <FilterDialog
        open={filterDialogOpen}
        filters={filters}
        onClose={() => setFilterDialogOpen(false)}
        paginator={paginator}
        onFilterApplied={onFilterApplied}
      />
      <ToolbarTitle>
        {numSelected > 0 && bulkActions ? (
          <>
            <Button variant="contained" color="primary" onClick={handleClick}>
              {numSelected} selected
              <ArrowDropDownIcon />
            </Button>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
              {bulkActions &&
                bulkActions.map(({ label, isEnabled, tooltip }, index) => {
                  if (label === 'divider') {
                    return <Divider key={`divider.${index}`} />
                  }
                  return (
                    <Tooltip key={`tooltip.${label}`} title={tooltip ?? ''}>
                      <MenuItem
                        disabled={isEnabled !== undefined ? !isEnabled : false}
                        onClick={() => {
                          if (onSelectedBulkAction) {
                            onSelectedBulkAction(label)
                          }
                          handleClose()
                        }}
                      >
                        {label}
                      </MenuItem>
                    </Tooltip>
                  )
                })}
            </Menu>
          </>
        ) : (
          <Typography variant="h6" id="tableTitle">
            {title}
          </Typography>
        )}
      </ToolbarTitle>
      <Spacer />
      <Grid container spacing={1} alignItems="flex-end" justify="flex-end">
        {actions &&
          actions.map(({ title, icon, onClick }) => (
            <Grid item key={`action.${title}`}>
              {icon ? (
                <IconButton onClick={onClick}>{icon}</IconButton>
              ) : (
                <Button onClick={onClick}>{title}</Button>
              )}
            </Grid>
          ))}
        {exportConfig && (
          <Grid item>
            <IconButton aria-label="filter" onClick={() => setExportDialogOpen(true)}>
              <GetAppIcon />
            </IconButton>
          </Grid>
        )}
        <Grid item>
          <TextField
            id="standard-search"
            label="Search"
            type="search"
            value={searchTerm}
            onChange={(e: React.ChangeEvent<any>) => {
              const term = e.target.value
              setSearchTerm(term)
              onSearchChange.callback(term)
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        {filters && (
          <Grid item>
            <IconButton aria-label="filter" onClick={() => setFilterDialogOpen(true)}>
              <FilterListIcon />
            </IconButton>
          </Grid>
        )}
      </Grid>
      {exportConfig && (
        <ExportDialog
          name={title}
          download={exportConfig?.exportAPI}
          open={exportDialogOpen}
          fields={exportConfig?.exportFields}
          exportFilters={exportConfig?.exportFilters}
          handleClose={() => setExportDialogOpen(false)}
        />
      )}
    </Toolbar>
  )
}

interface TablePaginationProps {
  paginator: Paginator
}

const TablePagination = (props: TablePaginationProps) => {
  const { paginator } = props
  return (
    <Grid container spacing={6} mt={1} alignItems="center">
      <Grid item style={{ marginLeft: '8px' }}>
        <IconButton size="small" onClick={() => paginator.refresh()}>
          <RefreshIcon />
        </IconButton>
      </Grid>
      <Grid item>
        <Pagination
          shape="rounded"
          size="small"
          siblingCount={2}
          count={Math.ceil(paginator.data.count / (paginator.pageSize as number))}
          page={paginator.page}
          onChange={paginator.handlePageChange}
        />
      </Grid>
      <Grid item>
        <span>
          Showing {paginator.firstIndex.toLocaleString()} - {paginator.lastIndex.toLocaleString()}{' '}
          of {paginator.data.count.toLocaleString()}
        </span>
      </Grid>
    </Grid>
  )
}

type Alignment = 'left' | 'center' | 'right'

export interface FieldSpec {
  id: string
  label: string
  ordering?: string
  alignment?: Alignment
  renderer: React.FC<{ row: any; value: any }>
}

export interface Action {
  label: string
  action: (rows: any[]) => void
  isEnabled?: boolean
  tooltip?: string
}

interface PaginatedTableProps {
  title: string
  fields: FieldSpec[]
  bulkActions?: Action[]
  paginator: Paginator
  actions?: { title: string; icon?: JSX.Element; onClick: () => void }[]
  rowActions?: RowAction[]
  filters?: Filter[]
  exportConfig?: ExportConfig
  onSelectedItem?: (selected: any[]) => void
}

const PaginatedTable = (props: PaginatedTableProps) => {
  const {
    fields,
    paginator,
    title,
    bulkActions,
    actions,
    rowActions,
    filters,
    exportConfig,
    onSelectedItem,
  } = props
  const [order, setOrder] = useState<Ordering>('none')
  const [orderBy, setOrderBy] = useState<Identifier>('customer')
  const [selected, setSelected] = useState<Identifier[]>([])
  const [selectedObjects, setSelectedObjects] = useState<Map<Identifier, any>>(new Map())
  const initial = paginator.data.results.map(() => null)
  const [anchorEls, setAnchorEls] = useState<(null | HTMLElement)[]>(initial)

  const rowMenuOpen = (index: number) => {
    return (event: React.MouseEvent<HTMLButtonElement>) => {
      anchorEls[index] = event.currentTarget
      setAnchorEls([...anchorEls])
    }
  }
  const rowMenuClose = (index: number) => {
    return () => {
      anchorEls[index] = null
      setAnchorEls([...anchorEls])
    }
  }

  useEffect(() => {
    onSelectedItem?.(Array.from(selectedObjects.values()))
  }, [selectedObjects, onSelectedItem])

  const handleRequestSort = (event: React.SyntheticEvent, property: Identifier) => {
    const newOrder = nextOrder[order]
    if (property === orderBy) {
      setOrder(newOrder)
    } else {
      setOrder('asc')
      setOrderBy(property)
    }
    if (newOrder === 'none') {
      paginator.patchFilters({ ordering: null })
    } else {
      const orderingStr = newOrder === 'desc' ? '-' : ''
      paginator.patchFilters({ ordering: `${orderingStr}${property}` })
    }
  }

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = paginator.data.results.map((n) => n.guid)
      setSelected(newSelecteds)
      const newSelectedObject: Array<[Identifier, any]> = paginator.data.results.map((n) => [
        n.guid as string,
        n,
      ])
      setSelectedObjects(new Map(newSelectedObject))
    } else {
      setSelected([])
      setSelectedObjects(new Map())
    }
  }

  const handleFilterApplied = () => {
    setSelected([])
    setSelectedObjects(new Map())
  }

  const handleBulkAction = (selectedAction: string) => {
    if (bulkActions) {
      const selectedBulkAction = bulkActions.find(
        (action) => action.label === selectedAction
      )?.action
      if (selectedBulkAction) {
        selectedBulkAction(Array.from(selectedObjects.values()))
      }
    }
  }

  const handleClick = (event: React.SyntheticEvent, id: Identifier, row: any) => {
    const selectedIndex = selected.indexOf(id)
    let newSelected: Identifier[] = []
    const newSelectedObject = selectedObjects

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id)
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1))
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1))
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      )
    }

    if (newSelectedObject.has(id)) {
      newSelectedObject.delete(id)
    } else {
      newSelectedObject.set(id, row)
    }

    setSelected(newSelected)
    setSelectedObjects(new Map(newSelectedObject))
  }

  const isSelected = (id: Identifier) => selected.indexOf(id) !== -1

  if (!paginator.data) {
    paginator.data = {
      count: 0,
      results: [],
    }
  }

  return (
    <div>
      <Paper>
        <TableToolbar
          title={title}
          numSelected={selected.length}
          actions={actions}
          bulkActions={bulkActions}
          onSelectedBulkAction={handleBulkAction}
          filters={filters}
          exportConfig={exportConfig}
          onFilterApplied={handleFilterApplied}
          paginator={paginator}
        />
        {paginator.fetching && (
          <div>
            <LinearProgress />
          </div>
        )}
        <TablePagination paginator={paginator} />
        <TableContainer>
          <Table aria-labelledby="tableTitle" size={'small'} aria-label="enhanced table">
            <GenericTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              fields={fields}
              bulkActions={bulkActions}
              rowActions={rowActions}
              rowCount={paginator.data.results.length}
              onSelectedItem={onSelectedItem}
            />
            <TableBody>
              {paginator.data.count ? (
                paginator.data.results.map((row, index) => {
                  const isItemSelected = isSelected(row.guid)
                  const labelId = `enhanced-table-checkbox-${index}`

                  return (
                    <TableRow
                      key={`row.${row.guid}.${index}`}
                      hover
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      selected={isItemSelected}
                    >
                      {(onSelectedItem || bulkActions) && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isItemSelected}
                            inputProps={{ 'aria-labelledby': labelId }}
                            onClick={(event) => handleClick(event, row.guid, row)}
                          />
                        </TableCell>
                      )}
                      {fields.map((field, fieldIndex) => {
                        if (!field) {
                          return (
                            <TableCell key={`cell.misconfigured.${fieldIndex}`} component="th" id={labelId} scope="row">
                              -
                            </TableCell>
                          )
                        }
                        const path = field.id.split('.')
                        let value = row
                        for (let i = 0; i < path.length; i += 1) {
                          if (!value || !(path[i] in value)) {
                            value = null
                            break
                          }
                          value = value[path[i]]
                        }
                        return (
                          <TableCell
                            key={`cell.${row.guid}.${index}.${field.id}`}
                            component="th"
                            id={labelId}
                            scope="row"
                          >
                            <field.renderer row={row} value={value} />
                          </TableCell>
                        )
                      })}
                      {rowActions && (
                        <TableCell component="th" id={labelId} scope="row">
                          <IconButton color="primary" onClick={rowMenuOpen(index)}>
                            <MoreVertIcon />
                          </IconButton>
                          <Menu
                            anchorEl={anchorEls[index]}
                            open={Boolean(anchorEls[index])}
                            onClose={rowMenuClose(index)}
                          >
                            {rowActions.map((rowAction, actionIndex) => (
                              <MenuItem
                                key={`action.${actionIndex}`}
                                onClick={() => {
                                  rowMenuClose(index)()
                                  rowAction.onClick(row)
                                }}
                              >
                                {isString(rowAction.title) ? rowAction.title : rowAction.title(row)}
                              </MenuItem>
                            ))}
                          </Menu>
                        </TableCell>
                      )}
                    </TableRow>
                  )
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={rowActions ? fields.length + 1 : fields.length}>
                    No records.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination paginator={paginator} />
      </Paper>
    </div>
  )
}

export default PaginatedTable
